import React, { useEffect } from 'react';
import { FormInput, IFormInputProps } from '../../../components/merchants/FormInput';
import { useTranslation } from 'react-i18next';
import PCICheckbox from '../../../components/application/PCICheckbox';
import { useMerchants } from '../../../store/merchnts/merchants-context';
import { SectionWrapper } from '../../../components/SectionWrapper';
import { IModalWindow, ModalWindow } from '../../../components/modal/ModalWindow';
import { UpdateComponent } from '../../../components/merchants/MerchantPage/withMerchantDetails';
import { fetchMccList } from '../../../store/merchnts/actions/merchant';
import { fetchAgencyList } from '../../../store/merchnts/actions/agents';
import { FormCheckbox } from '../../../components/application/FormCheckbox';
import { IAuthState } from '../../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../../setup';
import { checkRoles, CLAIMS } from '../../../utils/roles-helper';
import {
	fetchIntegrationPartnersList,
	fetchWhiteListedPartnersList,
} from '../../../store/merchnts/actions/partners';
import {
	INTEGRATION_PARTNER_BUSINESS_INFORMATION_INPUTS,
	PARTNERS_BUSINESS_INFORMATION_INPUTS,
	SUPER_ADMIN_BUSINESS_INFORMATION_INPUTS,
} from '../../../constants/merchant/business-inputs';
import { BUSINESS_FORM_FIELDS, BUSINESS_FORM_FIELDS_NAMES } from '../../../constants/merchant/business-form';
import { InputType } from '../../../components/form/form-input-constants';
import { MerchantsBusinessMetrics } from '../../../components/merchants/MerchantPage/MerchantsBusinessMetrics';

interface IBusinessProps {
	isEditMode: boolean;
	setIsEditMode: (value:boolean) => void;
	isLoading: boolean;
	modalState: IModalWindow;
	closeModal: () => void;
	formik: any;
	onEditClick: () => void;
	onCancelClick?: () => void;
	setModalState: (value: any) => void;
}

const MerchantBusinessPage = (props: IBusinessProps): React.ReactNode => {
	const { isEditMode, setIsEditMode, isLoading, modalState, closeModal, formik, onEditClick, onCancelClick } = props;
	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
	const {
		merchantState: {
			merchantData: data,
			isMerchantDataLoading,
			merchantsLists: lists,
			whitelabelPartnersListSelector,
			isWhitelabelPartnersListSelectorLoading,
			integrationPartnersListSelector,
			isIntegrationPartnersListSelectorLoading,
			mccList,
			salesAgencyListSelector,
			accountListSelector,
		},
		dispatch
	} = useMerchants();
	const { t } = useTranslation();

	// TODO check partners permissions
	const BUSINESS_INPUTS = (): IFormInputProps[] => {
		switch (true) {
			case checkRoles(authUser, CLAIMS.SUPER_ADMIN):
				return SUPER_ADMIN_BUSINESS_INFORMATION_INPUTS({
					partnerList: whitelabelPartnersListSelector,
					integrationPartnersListSelector,
					salesAgencyListSelector,
					lists,
					t,
					mccList,
					accountListSelector,
					data,
					authUser
				});
			case checkRoles(authUser, CLAIMS.PARTNER_ADMIN):
				return PARTNERS_BUSINESS_INFORMATION_INPUTS({
          integrationPartnersListSelector,
          lists,
          t,
          mccList,
          accountListSelector,
          data,
        });
			case checkRoles(authUser, CLAIMS.INTEGRATION_PARTNER_ADMIN):
				return INTEGRATION_PARTNER_BUSINESS_INFORMATION_INPUTS({
					lists,
					t,
					mccList,
					accountListSelector,
					data,
				});
		}
	};

	useEffect(() => {
		if (checkRoles(authUser, CLAIMS.SUPER_ADMIN)) {
			void fetchAgencyList(dispatch);
		}
	}, []);

	useEffect(() => {
		void fetchMccList(dispatch);
	}, []);

	useEffect(() => {
		if (checkRoles(authUser, CLAIMS.SUPER_ADMIN)) {
			void fetchWhiteListedPartnersList(dispatch);
		}
		if (checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN)) {
			void fetchIntegrationPartnersList(dispatch);
		}
	}, [lists]);

	const pageLoading = isMerchantDataLoading || isWhitelabelPartnersListSelectorLoading || isIntegrationPartnersListSelectorLoading;
	const showPlaceholder = !data || typeof data === 'object' && Object.keys(data).length === 0;

	return (
		<>
			<SectionWrapper
				showPlaceholder={showPlaceholder}
				title={t('MERCHANT.BUSINESS_INFO')}
				sectionAdditionalClass="mb-5 min-height-200"
				isEditMode={isEditMode}
				setIsEditMode={setIsEditMode}
				onEditClick={onEditClick}
				onCancelClick={onCancelClick}
				loading={isLoading}
				pageLoading={pageLoading}
				isCardBody
				isEditBlock={checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN) && !showPlaceholder}
			>
				<div className="row">
					{
						BUSINESS_INPUTS().map(item => {
							return (
								<FormInput
									{...item}
									key={item.key}
									formik={formik}
									isEditMode={isEditMode}
									// setCreateNew={setCreateNew}
									suffix={item.suffix}
								/>
							);
						})
					}
					{checkRoles(authUser, CLAIMS.INTEGRATION_PARTNER_ADMIN) && (
						<PCICheckbox
							formEditStatus={isEditMode}
							formik={formik}
						/>
					)}
					<FormInput
						formik={formik}
						key={BUSINESS_FORM_FIELDS.WEB_URL}
						fieldName={BUSINESS_FORM_FIELDS.WEB_URL}
						label={t(BUSINESS_FORM_FIELDS_NAMES.WEB_URL)}
						placeholder={`${t('NEW_APPLICATION.ENTER')} ${t(BUSINESS_FORM_FIELDS_NAMES.WEB_URL)}`}
						inputType={InputType.Website}
						required
						isEditMode={isEditMode}
						// additionalClasses="col-12 col-md-6"
					/>

					<FormInput
						formik={formik}
						key={BUSINESS_FORM_FIELDS.CRM_ID}
						fieldName={BUSINESS_FORM_FIELDS.CRM_ID}
						label={t(BUSINESS_FORM_FIELDS_NAMES.CRM_ID)}
						placeholder={`${t('NEW_APPLICATION.ENTER')} ${t(BUSINESS_FORM_FIELDS_NAMES.CRM_ID)}`}
						inputType={InputType.Text}
						isEditMode={isEditMode}
						// additionalClasses="col-12 col-md-6"
					/>

					<FormInput
						formik={formik}
						key={BUSINESS_FORM_FIELDS.EXTERNAL_MERCHANT_IDENTIFIER}
						fieldName={BUSINESS_FORM_FIELDS.EXTERNAL_MERCHANT_IDENTIFIER}
						label={t(BUSINESS_FORM_FIELDS_NAMES.EXTERNAL_MERCHANT_IDENTIFIER)}
						placeholder={`${t('NEW_APPLICATION.ENTER')} ${t(BUSINESS_FORM_FIELDS_NAMES.EXTERNAL_MERCHANT_IDENTIFIER)}`}
						inputType={InputType.Text}
						isEditMode={isEditMode}
						// additionalClasses="col-12 col-md-6"
					/>

					<FormCheckbox
						disabled={!isEditMode}
						formik={formik}
						fieldName={BUSINESS_FORM_FIELDS.SITE_VERIFICATION}
						label={BUSINESS_FORM_FIELDS_NAMES.SITE_VERIFICATION}
            additionalClasses="mt-7"
					/>

					{checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN) && (
						<PCICheckbox
							formEditStatus={isEditMode}
							formik={formik}
						/>
					)}

					<FormCheckbox
						disabled={!isEditMode}
						formik={formik}
						fieldName={BUSINESS_FORM_FIELDS.INTEGRATION_PARTNER_PARTICIPATE_SALE}
						label={BUSINESS_FORM_FIELDS_NAMES.INTEGRATION_PARTNER_PARTICIPATE_SALE}
						additionalClasses="mt-7"
					/>
				</div>

				<MerchantsBusinessMetrics formik={formik} isEditMode={isEditMode} />

			</SectionWrapper>
			<ModalWindow
				{...modalState}
				onClose={closeModal}
			/>
		</>
	);
};

export default UpdateComponent(MerchantBusinessPage, 'business-information');
